import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Container, Table, Button, Form, Nav, InputGroup, Spinner, Alert } from 'react-bootstrap';
import './Statistics.css';
import './StatisticsSettings.css';
import { confirmDelete } from '../components/ConfirmDialog';
import { MUTACE_POZNAMKY, KLASIFIKACE_MEDII } from './statisticsSettingsMockData';
import {
  nactiNastaveni,
  nactiMedia,
  ulozSazby,
  ulozTitul,
  ulozObchodnika,
  ulozManazera,
  pridejManazera,
  ulozMentoring,
  smazMentoring,
  ulozMedium,
  nactiVydaniTitulu,
  ulozPlanVydani,
  zrusPlanVydani,
} from './statisticsSettingsApi';

/* ==========================================================================
   Statistiky → Nastavení sazeb a číselníků

   Obrazovka je napojená na API (`/api/statistics/settings`). Překlad mezi
   databázovým tvarem a tvary, se kterými pracuje tohle JSX, dělá
   statisticsSettingsApi.js.

   Ze statisticsSettingsMockData.js zůstávají jen dvě čistě popisné konstanty
   (vysvětlivky k mutacím a ke klasifikaci médií) — ty do DB nepatří.

   Sazby jsou verzované: uložení nezapisuje do stávající sady, ale zakládá
   novou s platností od zvoleného data. Uzavřená období se tak dají přepočítat
   sazbami, které pro ně platily.
   ========================================================================== */

const TABY = [
  { id: 'procenta', ikona: 'bi-percent', nazev: 'Procenta provizí' },
  { id: 'bonus', ikona: 'bi-award', nazev: 'Bonusová pásma' },
  { id: 'plany', ikona: 'bi-bullseye', nazev: 'Plány titulů' },
  { id: 'slevy', ikona: 'bi-tag', nazev: 'Maximální slevy' },
  { id: 'splatnost', ikona: 'bi-calendar-x', nazev: 'Korekce splatnosti' },
  { id: 'media', ikona: 'bi-collection', nazev: 'Klasifikace médií' },
];

/* ------------------------------------------------------------ formátování */
const fmtKc = (v) => `${Math.round(v || 0).toLocaleString('cs-CZ')} Kč`;
const fmtPct = (v, dec = 1) =>
  `${((v || 0) * 100).toLocaleString('cs-CZ', { minimumFractionDigits: dec, maximumFractionDigits: dec })} %`;

const toast = (message, variant = 'success') =>
  window.dispatchEvent(new CustomEvent('app:toast', { detail: { message, variant } }));

const cislo = (raw) => {
  const v = Number(String(raw).replace(',', '.').trim());
  return isNaN(v) ? 0 : v;
};

/** Editovatelná buňka — commit na blur / Enter, stejně jako v sestavě provizí. */
function CellEdit({ value, onCommit, step = '1', placeholder, title, className = '' }) {
  return (
    <input
      key={value}
      type="number"
      step={step}
      className={`cell-edit ${className}`}
      defaultValue={value}
      placeholder={placeholder}
      title={title}
      onBlur={(e) => onCommit(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
    />
  );
}

/** Hlavička sekce uvnitř záložky. */
function Karta({ nadpis, popis, akce, children }) {
  return (
    <>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 karta-head">
        <div>
          <h6 className="fw-semibold mb-0">{nadpis}</h6>
          {popis && <div className="small-muted">{popis}</div>}
        </div>
        {akce && <div className="d-flex gap-2">{akce}</div>}
      </div>
      {children}
    </>
  );
}

/** Datum z API (2026-05-01) na tvar, jaký obrazovka zobrazuje (01.05.2026). */
/** Posun data o celé měsíce, `Y-m-d`. */
const oMesice = (datum, mesicu) => {
  const d = new Date(datum);
  d.setMonth(d.getMonth() + mesicu);
  return d.toISOString().substring(0, 10);
};

/**
 * Okno vydání: rok zpátky a rok dopředu od dneška, posunutelné po rocích.
 * Tituly mají hodně vydání (Klíč 106, www.novinykraje.cz 1 510), takže výpis
 * všech je až na vyžádání.
 */
const oknoVydani = (posun = 0) => {
  const dnes = new Date().toISOString().substring(0, 10);
  return { od: oMesice(dnes, -12 + posun * 12), do: oMesice(dnes, 12 + posun * 12) };
};

const naCesky = (iso) => {
  if (!iso) return '—';
  const [r, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}.${m}.${r}`;
};

function StatisticsSettings() {
  const [aktivni, setAktivni] = useState('procenta');
  const [nacitam, setNacitam] = useState(true);
  const [chyba, setChyba] = useState('');

  /* číselníky — pracovní kopie; uložení jde do API explicitně */
  const [platnostOd, setPlatnostOd] = useState('');
  const [novaPlatnost, setNovaPlatnost] = useState('');
  const [pasma, setPasma] = useState([]);
  const [zaklady, setZaklady] = useState({ partner: 0.12, special: 0.10, penalizace: 0.005, min: 0 });
  const [bonusPasma, setBonusPasma] = useState([]);
  const [obchodnici, setObchodnici] = useState([]);
  const [manazeri, setManazeri] = useState([]);
  const [typyMedii, setTypyMedii] = useState([]);
  /* Koho přidat jako manažera (výběr nad tabulkou týmů). */
  const [novyManazer, setNovyManazer] = useState('');
  /* Mentoring (migrace 052) + rozepsaná nová dvojice ve formuláři. */
  const [mentoring, setMentoring] = useState([]);
  const [novyMentoring, setNovyMentoring] = useState({
    mentorovanyId: '', mentorId: '', pct: 1, od: new Date().toISOString().slice(0, 10), do: '',
  });
  /* Neaktivní obchodníci jsou schovaní — stejně jako ve výběru v Provizích.
     Ze 87 lidí v číselníku jich dnes pracuje 27 a zbytek jen protahoval
     tabulku. Tlačítko pod tabulkou je zobrazí. */
  const [neaktivniVidet, setNeaktivniVidet] = useState(false);
  /* Koho zrovna vypnul uživatel — ten na obrazovce zůstane, dokud se
     nastavení nenačte znovu, aby mu řádek nezmizel pod rukama. */
  const [pripnuti, setPripnuti] = useState(() => new Set());
  const [tituly, setTituly] = useState([]);
  const [mutacePlany, setMutacePlany] = useState({});
  const [velikostiMutaci, setVelikostiMutaci] = useState([]);
  const [mutace, setMutace] = useState([]);
  const [korekce, setKorekce] = useState([]);
  const [nezarazenaMedia, setNezarazenaMedia] = useState(0);

  /* rozbalená vydání titulu (plány jednotlivých vydání) */
  const [rozbalenyTitul, setRozbalenyTitul] = useState(null);
  const [vydani, setVydani] = useState(null);
  const [nacitamVydani, setNacitamVydani] = useState(false);
  const [oknoPosun, setOknoPosun] = useState(0);
  const [vsechnaVydani, setVsechnaVydani] = useState(false);

  /* filtr v záložce Maximální slevy */
  const [hledatSlevy, setHledatSlevy] = useState('');
  const [typSlevy, setTypSlevy] = useState('');

  /* ---------------------------------------------------------- načtení --- */
  const nacti = useCallback(async () => {
    setNacitam(true);
    setChyba('');
    try {
      const d = await nactiNastaveni();
      setPlatnostOd(d.platnostOd);
      setPasma(d.pasma);
      setZaklady(d.zaklady);
      setBonusPasma(d.bonusPasma);
      setObchodnici(d.obchodnici);
      setPripnuti(new Set());
      setManazeri(d.manazeri);
      setTypyMedii(d.typyMedii || []);
      setMentoring(d.mentoring || []);
      setTituly(d.tituly);
      setMutacePlany(d.mutacePlany);
      setVelikostiMutaci(d.velikostiMutaci);
      setKorekce(d.korekce);
      setNezarazenaMedia(d.nezarazenaMedia);

      /* Mutace sdružených titulů = média, která pod ně spadají. */
      const media = await nactiMedia();
      const sdruzene = new Set(d.tituly.filter(t => t.planZMutaci || t.pocetMedii > 1).map(t => t.id));
      setMutace(media
        .filter(m => m.titulKod && sdruzene.has(m.titulKod))
        .map(m => ({
          id: m.id, titulId: m.titulKod, nazev: m.nazev,
          velikost: m.velikost || '', stran: m.stran,
        })));
    } catch (e) {
      setChyba(e.message || 'Nastavení se nepodařilo načíst.');
    } finally {
      setNacitam(false);
    }
  }, []);

  useEffect(() => { nacti(); }, [nacti]);

  /* ------------------------------------------------------------- akce --- */

  /** Uloží číselník jako novou verzi sazeb (nebo do existující, když datum sedí). */
  const ulozCast = async (cast, hlaska) => {
    try {
      await ulozSazby(platnostOd, cast);
      toast(hlaska);
      await nacti();
    } catch (e) {
      toast(e.message || 'Uložení selhalo.', 'danger');
    }
  };

  const handleNovaVerze = async () => {
    if (!novaPlatnost) { toast('Zvolte datum platnosti nové verze.', 'warning'); return; }
    try {
      await ulozSazby(novaPlatnost, { pasma, zaklady, bonusPasma, korekce, mutacePlany });
      toast(`Vytvořena nová verze sazeb platná od ${naCesky(novaPlatnost)}.`);
      setNovaPlatnost('');
      await nacti();
    } catch (e) {
      toast(e.message || 'Novou verzi se nepodařilo vytvořit.', 'danger');
    }
  };

  /** Kód titulu (T-NK) na jeho databázové id — API pracuje s číselným. */
  const titulDbId = (kod) => tituly.find(t => t.id === kod)?.dbId ?? null;

  /** Individuální nastavení obchodníka se ukládá ihned. */
  const zmenObchodnika = (id, zmena) => {
    if (zmena.aktivni === false) {
      setPripnuti(prev => new Set(prev).add(id));
    }
    setObchodnici(prev => prev.map(o => (o.id === id ? { ...o, ...zmena } : o)));
    ulozObchodnika(id, zmena)
      .then(() => toast('Nastavení obchodníka uloženo.'))
      .catch(e => toast(e.message || 'Uložení selhalo.', 'danger'));
  };

  /* Kdo se v tabulce individuálních úprav vypíše. Vypnutí přepínače
     „Aktivní" řádek nechá na místě až do příštího načtení — jinak by člověku
     zmizel pod rukama hned, jak ho vypne. */
  const obchodniciVidet = useMemo(
    () => (neaktivniVidet ? obchodnici : obchodnici.filter(o => o.aktivni || pripnuti.has(o.id))),
    [obchodnici, neaktivniVidet, pripnuti],
  );
  const neaktivnich = useMemo(() => obchodnici.filter(o => !o.aktivni).length, [obchodnici]);

  const zmenManazera = (id, zmena) => {
    setManazeri(prev => prev.map(m => (m.id === id ? { ...m, ...zmena } : m)));
    ulozManazera(id, zmena)
      .then(() => toast('Nastavení manažera uloženo.'))
      .catch(e => toast(e.message || 'Uložení selhalo.', 'danger'));
  };

  /** Uložení mentoringu posílá vždy celou dvojici — server ji validuje jako celek. */
  const zmenMentoring = async (radek, zmena) => {
    const novy = { ...radek, ...zmena };
    setMentoring(prev => prev.map(m => (m.id === radek.id ? novy : m)));
    try {
      await ulozMentoring(radek.id, novy);
      toast('Mentoring uložen.');
      await nacti();
    } catch (e) {
      toast(e.message || 'Uložení selhalo.', 'danger');
      await nacti();
    }
  };

  const handlePridejMentoring = async () => {
    const n = novyMentoring;
    if (!n.mentorovanyId || !n.mentorId) { toast('Vyber mentorovaného i mentora.', 'warning'); return; }
    try {
      await ulozMentoring(null, {
        mentorovanyId: Number(n.mentorovanyId),
        mentorId: Number(n.mentorId),
        pct: cislo(n.pct) / 100,
        od: n.od,
        do: n.do || null,
      });
      setNovyMentoring({
        mentorovanyId: '', mentorId: '', pct: 1, od: new Date().toISOString().slice(0, 10), do: '',
      });
      toast('Mentoring přidán.');
      await nacti();
    } catch (e) {
      toast(e.message || 'Mentoring se nepodařilo přidat.', 'danger');
    }
  };

  const handleSmazMentoring = async (radek) => {
    const ok = await confirmDelete({
      title: 'Zrušit mentoring?',
      message: `${radek.mentor} přestane dostávat ${(radek.pct * 100).toFixed(2)} % z úhrad `
        + `${radek.mentorovany}. Spočítané a uzavřené sestavy to nemění — do otisku se zapsaly.`,
      confirmText: 'Zrušit mentoring',
    });
    if (!ok) return;
    try {
      await smazMentoring(radek.id);
      toast('Mentoring zrušen.');
      await nacti();
    } catch (e) {
      toast(e.message || 'Smazání selhalo.', 'danger');
    }
  };

  const handlePridejManazera = async () => {
    if (!novyManazer) { toast('Vyberte obchodníka.', 'warning'); return; }
    try {
      await pridejManazera(Number(novyManazer));
      setNovyManazer('');
      toast('Manažer přidán. Doplňte mu sazbu a tým.');
      await nacti();
    } catch (e) {
      toast(e.message || 'Manažera se nepodařilo přidat.', 'danger');
    }
  };

  /**
   * Změna titulu se ukládá rovnou — je to vlastnost titulu, ne sazebníku.
   *
   * POZOR na tvar: nová hodnota se počítá ze **současného stavu**, ne uvnitř
   * updateru `setTituly(prev => …)`. React updater nevolá synchronně, takže
   * hodnota vytažená z jeho těla je v okamžiku volání API ještě prázdná
   * a request by se vůbec neodeslal.
   */
  const ulozZmenuTitulu = (id, uprav, volby = {}) => {
    const soucasny = tituly.find(t => t.id === id);
    if (!soucasny) return;

    const upraveny = uprav(soucasny);
    setTituly(prev => prev.map(t => (t.id === id ? upraveny : t)));
    ulozTitul(upraveny, volby).catch(e => toast(e.message || 'Uložení selhalo.', 'danger'));
  };

  const upravTitul = (id, zmena, volby = {}) =>
    ulozZmenuTitulu(id, t => ({ ...t, ...zmena }), volby);

  const upravPlan = (id, pole, raw) =>
    ulozZmenuTitulu(id, t => {
      const plany = { ...t.plany, [pole]: cislo(raw) };
      return { ...t, plany, planNeurcen: !(plany.p11 || plany.p14) };
    }, { plan: true });

  const upravSlevu = (id, pole, raw) =>
    ulozZmenuTitulu(id, t => ({ ...t, slevy: { ...t.slevy, [pole]: cislo(raw) } }), { slevy: true });

  const naseTituly = useMemo(() => tituly.filter(t => t.typ === 'nase'), [tituly]);

  const titulySeSlevami = useMemo(() => {
    const q = hledatSlevy.trim().toLowerCase();
    return tituly.filter(t => {
      if (!t.slevy || !Object.keys(t.slevy).length) return false;
      if (typSlevy && t.typ !== typSlevy) return false;
      if (!q) return true;
      return `${t.nazev} ${t.kraj} ${t.lokalita}`.toLowerCase().includes(q);
    });
  }, [tituly, hledatSlevy, typSlevy]);

  /* Plán Novin kraje = součet plánů jeho mutací (+ kompletová inzerce). */
  const soucetPlanuNK = useMemo(
    () => mutace
      .filter(m => m.titulId === 'T-NK')
      .reduce((s, m) => s + (mutacePlany[m.velikost]?.p14 || 0), 0),
    [mutace, mutacePlany],
  );

  const pocetPodleTypu = (typ) => tituly.filter(t => t.typ === typ).length;

  /* ============================================================ ZÁLOŽKY == */

  /* -------------------------------------------- 1) procenta provizí ----- */
  const zalozkaProcenta = () => (
    <Karta nadpis="Procenta provizí z vydání">
      <div className="row g-4">
        <div className="col-12 col-xl-6">
          <div className="sec-title">Naše média – pásma podle plnění plánu vydání</div>
          <Table hover className="align-middle stat-table">
            <thead className="table-light">
              <tr>
                <th>Úroveň plnění</th>
                <th className="num">Provize %</th>
                <th>Popis</th>
              </tr>
            </thead>
            <tbody>
              {pasma.map((p, i) => (
                <tr key={p.kod}>
                  <td><span className={`tier-badge t-${p.kod}`}>{p.kod} %</span></td>
                  <td className="num editable">
                    <CellEdit
                      value={(p.pct * 100).toFixed(1)}
                      step="0.5"
                      onCommit={(v) => {
                        setPasma(prev => prev.map((x, j) => (j === i ? { ...x, pct: cislo(v) / 100 } : x)));
                        toast('Sazba upravena.');
                      }}
                    />
                  </td>
                  <td className="small-muted">{p.nazev}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className="col-12 col-xl-6">
          <div className="sec-title">Partnerská média a speciální operace</div>
          <Table hover className="align-middle stat-table">
            <thead className="table-light">
              <tr>
                <th>Typ média</th>
                <th className="num">Základní provize %</th>
                <th>Bonus</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="badge-soft b-partner"><i className="bi bi-share"></i>Partnerská média</span></td>
                <td className="num editable">
                  <CellEdit
                    value={(zaklady.partner * 100).toFixed(1)}
                    step="0.5"
                    onCommit={(v) => { setZaklady(z => ({ ...z, partner: cislo(v) / 100 })); toast('Základní sazba upravena.'); }}
                  />
                </td>
                <td className="small-muted">nepřičítá se (vždy 0 %)</td>
              </tr>
              <tr>
                <td><span className="badge-soft b-special"><i className="bi bi-box-seam"></i>Speciální operace</span></td>
                <td className="num editable">
                  <CellEdit
                    value={(zaklady.special * 100).toFixed(1)}
                    step="0.5"
                    onCommit={(v) => { setZaklady(z => ({ ...z, special: cislo(v) / 100 })); toast('Základní sazba upravena.'); }}
                  />
                </td>
                <td className="small-muted">nepřičítá se (vždy 0 %)</td>
              </tr>
            </tbody>
          </Table>

          <div className="sec-title mt-4">Penalizace za slevu nad maximum (partnerská média)</div>
          <div className="row g-3 align-items-end">
            <div className="col-auto">
              <label className="form-label">Snížení provize za každý 1 p.b. slevy nad maximum</label>
              <InputGroup style={{ width: '180px' }}>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={(zaklady.penalizace * 100).toFixed(1)}
                  onChange={(e) => setZaklady(z => ({ ...z, penalizace: cislo(e.target.value) / 100 }))}
                />
                <InputGroup.Text>p.b.</InputGroup.Text>
              </InputGroup>
            </div>
            <div className="col-auto">
              <label className="form-label">Minimální provize</label>
              <InputGroup style={{ width: '150px' }}>
                <Form.Control
                  type="number"
                  step="0.5"
                  value={(zaklady.min * 100).toFixed(1)}
                  onChange={(e) => setZaklady(z => ({ ...z, min: cislo(e.target.value) / 100 }))}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </div>
          </div>
        </div>
      </div>
    </Karta>
  );

  /* --------------------------------------------- 2) bonusová pásma ------ */
  const zalozkaBonus = () => (
    <Karta nadpis="Bonusová provize">
      <div className="row g-4">
        <div className="col-12 col-xl-5">
          <div className="sec-title">Pásma (společná pro všechny obchodníky)</div>
          <div className="table-responsive stat-table-wrap" style={{ maxHeight: '520px' }}>
            <Table hover className="align-middle stat-table">
              <thead className="table-light">
                <tr>
                  <th>Od obratu (Kč)</th>
                  <th className="num">Bonus %</th>
                  <th style={{ width: '48px' }}></th>
                </tr>
              </thead>
              <tbody>
                {bonusPasma.map((p, i) => (
                  <tr key={`${p.od}-${i}`}>
                    <td className="editable">
                      <CellEdit
                        value={p.od}
                        step="5000"
                        className="text-start"
                        onCommit={(v) => {
                          setBonusPasma(prev => prev
                            .map((x, j) => (j === i ? { ...x, od: cislo(v) } : x))
                            .sort((a, b) => a.od - b.od));
                          toast('Pásmo upraveno.');
                        }}
                      />
                    </td>
                    <td className="num editable">
                      <CellEdit
                        value={(p.pct * 100).toFixed(1)}
                        step="0.5"
                        onCommit={(v) => {
                          setBonusPasma(prev => prev.map((x, j) => (j === i ? { ...x, pct: cislo(v) / 100 } : x)));
                          toast('Pásmo upraveno.');
                        }}
                      />
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-danger"
                        className="btn-action"
                        title="Odstranit pásmo"
                        onClick={() => {
                          setBonusPasma(prev => prev.filter((_, j) => j !== i));
                          toast('Pásmo odstraněno.', 'warning');
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            className="mt-2"
            onClick={() => setBonusPasma(prev => {
              const posl = prev[prev.length - 1] || { od: 0, pct: 0 };
              return [...prev, { od: posl.od + 50000, pct: posl.pct + 0.005 }];
            })}
          >
            <i className="bi bi-plus-lg me-1"></i>Přidat pásmo
          </Button>
          <div className="chart-caption">Pod nejnižší hranicí je bonus 0 %.</div>
        </div>

        <div className="col-12 col-xl-7">
          <div className="sec-title">Individuální úpravy pro obchodníky</div>
          <div className="table-responsive">
            <Table hover className="align-middle stat-table">
              <thead className="table-light">
                <tr>
                  <th>Obchodník</th>
                  <th>Manažer</th>
                  <th className="num">Vlastní pásma</th>
                  <th className="num" style={{ minWidth: '120px' }}>Pevný bonus %</th>
                  <th>Nárok bez brány</th>
                  <th title="Obrat tohoto člověka nevstupuje do superprovize manažerů ani do plnění titulů pro jejich odměny">
                    Topmanagement
                  </th>
                  <th>Aktivní</th>
                </tr>
              </thead>
              <tbody>
                {obchodniciVidet.map(o => (
                  <tr key={o.id} className={o.aktivni ? '' : 'radek-vypnuty'}>
                    <td className="fw-semibold">{o.jmeno}</td>
                    <td>
                      <Form.Select
                        size="sm"
                        style={{ width: 'auto', display: 'inline-block' }}
                        value={o.manazerId || ''}
                        onChange={(e) => {
                          const manazerId = e.target.value ? Number(e.target.value) : null;
                          zmenObchodnika(o.id, { manazerId });
                        }}
                      >
                        <option value="">—</option>
                        {manazeri.map(m => <option key={m.id} value={m.id}>{m.jmeno}</option>)}
                      </Form.Select>
                    </td>
                    <td className="num small-muted">{o.pevnyBonus == null ? 'společná' : 'vlastní'}</td>
                    <td className="num editable">
                      <CellEdit
                        value={o.pevnyBonus == null ? '' : (o.pevnyBonus * 100).toFixed(1)}
                        step="0.5"
                        placeholder="—"
                        onCommit={(v) => zmenObchodnika(o.id, {
                          pevnyBonus: String(v).trim() === '' ? null : cislo(v) / 100,
                        })}
                      />
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={o.bezBrany}
                        onChange={(e) => zmenObchodnika(o.id, { bezBrany: e.target.checked })}
                      />
                    </td>
                    {/* Topmanagement (jednatel, účetní, obchodní ředitelka) —
                        jeho obrat se nepočítá do základu superprovize
                        podřízených ani do „plnění mimo topmanagement",
                        ze kterého je odměna manažera za gesci titulu. */}
                    <td>
                      <Form.Check
                        type="switch"
                        checked={!!o.topmanagement}
                        title={o.topmanagement
                          ? 'Nevstupuje do superprovize ani do plnění pro odměny manažerů'
                          : 'Běžný obchodník — jeho obrat se do superprovize počítá'}
                        onChange={(e) => zmenObchodnika(o.id, { topmanagement: e.target.checked })}
                      />
                    </td>
                    {/* Neaktivní obchodník zmizí z výběru v Provizích. Spočítané
                        sestavy to nijak nemění — jen se k němu přes nabídku
                        nedostaneš, dokud ho tady nezapneš zpátky. */}
                    <td>
                      <Form.Check
                        type="switch"
                        checked={o.aktivni}
                        title={o.aktivni
                          ? 'Nabízí se ve výběru obchodníka v Provizích'
                          : 'Ve výběru obchodníka v Provizích se nenabízí'}
                        onChange={(e) => zmenObchodnika(o.id, { aktivni: e.target.checked })}
                      />
                    </td>
                  </tr>
                ))}
                {!obchodniciVidet.length && (
                  <tr><td colSpan={7} className="text-center small-muted py-3">Žádný aktivní obchodník.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {!!neaktivnich && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="mt-2"
              onClick={() => setNeaktivniVidet(v => !v)}
            >
              <i className={`bi ${neaktivniVidet ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>
              {neaktivniVidet
                ? `Skrýt neaktivní (${neaktivnich})`
                : `Zobrazit neaktivní (${neaktivnich})`}
            </Button>
          )}
        </div>

        {/* ------------------------------------------- týmy a superprovize */}
        {/* Manažer bere procento z obratu svého týmu (superprovize). Tým se
            skládá sloupcem „Manažer" v tabulce vedle; tady se nastavuje
            sazba, rozsah a případné omezení na typ média — obchodní ředitel
            bere ze všech mimo topmanagement, manažer regionu jen ze svých
            lidí a jeden z nich jen z printu (migrace 051). */}
        <div className="col-12">
          <div className="sec-title mt-4">Týmy a superprovize</div>
          <div className="table-responsive">
            <Table hover className="align-middle stat-table">
              <thead className="table-light">
                <tr>
                  <th>Manažer</th>
                  <th>Role</th>
                  <th className="num" style={{ minWidth: '120px' }}>Superprovize %</th>
                  <th>Z koho</th>
                  <th>Jen z typu média</th>
                  <th className="num">Lidí v týmu</th>
                  <th className="num">Titulů v gesci</th>
                  <th>Aktivní</th>
                </tr>
              </thead>
              <tbody>
                {manazeri.map(m => {
                  const tym = obchodnici.filter(o => o.manazerId === m.id);
                  const cely = m.rozsah === 'company';
                  const vsichni = obchodnici.filter(o => o.aktivni && !o.topmanagement
                    && o.id !== m.obchodnikId);
                  return (
                    <tr key={m.id} className={m.aktivni === false ? 'radek-vypnuty' : ''}>
                      <td className="fw-semibold">{m.jmeno}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          defaultValue={m.role || ''}
                          placeholder="manažer regionu"
                          onBlur={(e) => {
                            const role = e.target.value.trim();
                            if (role !== (m.role || '')) zmenManazera(m.id, { role });
                          }}
                        />
                      </td>
                      <td className="num editable">
                        <CellEdit
                          value={(m.superprovize * 100).toFixed(2)}
                          step="0.1"
                          onCommit={(v) => zmenManazera(m.id, { superprovize: cislo(v) / 100 })}
                        />
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          style={{ width: 'auto', display: 'inline-block' }}
                          value={m.rozsah || 'team'}
                          onChange={(e) => zmenManazera(m.id, { rozsah: e.target.value })}
                        >
                          <option value="team">Jen z vlastního týmu</option>
                          <option value="company">Ze všech mimo topmanagement</option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          style={{ width: 'auto', display: 'inline-block' }}
                          value={m.typMedia || ''}
                          onChange={(e) => zmenManazera(m.id, { typMedia: e.target.value || null })}
                        >
                          <option value="">Bez omezení</option>
                          {typyMedii.map(t => (
                            <option key={t.id} value={t.id}>{t.nazev}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td
                        className="num"
                        title={cely
                          ? vsichni.map(o => o.jmeno).join(', ')
                          : (tym.map(o => o.jmeno).join(', ') || 'Nikdo — nastav mu lidi sloupcem „Manažer" v tabulce výše')}
                      >
                        {cely ? vsichni.length : tym.length}
                        {cely && <span className="small-muted"> (všichni)</span>}
                      </td>
                      <td className="num small-muted">{(m.gesce || []).length}</td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={m.aktivni !== false}
                          title={m.aktivni !== false
                            ? 'Superprovize se počítá'
                            : 'Vypnutý manažer — superprovize se nepočítá, gesce titulů zůstávají uložené'}
                          onChange={(e) => zmenManazera(m.id, { aktivni: e.target.checked })}
                        />
                      </td>
                    </tr>
                  );
                })}
                {!manazeri.length && (
                  <tr><td colSpan={8} className="text-center small-muted py-3">Žádní manažeři.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          <div className="d-flex align-items-center gap-2 mt-2">
            <Form.Select
              size="sm"
              style={{ maxWidth: '280px' }}
              value={novyManazer}
              onChange={(e) => setNovyManazer(e.target.value)}
            >
              <option value="">Přidat manažera — vyber obchodníka…</option>
              {obchodnici
                .filter(o => o.aktivni && !manazeri.some(m => m.obchodnikId === o.id))
                .map(o => <option key={o.id} value={o.id}>{o.jmeno}</option>)}
            </Form.Select>
            <Button variant="outline-primary" size="sm" onClick={handlePridejManazera}>
              <i className="bi bi-plus-lg me-1"></i>Přidat
            </Button>
          </div>
          <div className="chart-caption">
            Superprovize se počítá z úhrady bez DPH, kterou přinesli lidé z týmu, a
            <strong> nekrátí se za opožděnou platbu</strong>. Manažer sám ani topmanagement
            do základu nevstupují. V sestavě Provizí se faktury týmu objeví jako další řádky
            se sazbou manažera.
          </div>
        </div>

        {/* ------------------------------------------------------ mentoring */}
        {/* Zkušený obchodník zaučuje nováčka a bere z jeho úhrad procento.
            Od superprovize se to liší tím, že jde o dvojici lidí a že to
            k datu končí (migrace 052). */}
        <div className="col-12">
          <div className="sec-title mt-4">Mentoring</div>
          <div className="table-responsive">
            <Table hover className="align-middle stat-table">
              <thead className="table-light">
                <tr>
                  <th>Mentorovaný</th>
                  <th>Mentor</th>
                  <th className="num" style={{ minWidth: '110px' }}>Sazba %</th>
                  <th style={{ minWidth: '150px' }}>Od</th>
                  <th style={{ minWidth: '150px' }}>Do</th>
                  <th>Stav</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {mentoring.map(m => (
                  <tr key={m.id} className={m.stav === 'ended' ? 'radek-vypnuty' : ''}>
                    <td className="fw-semibold">{m.mentorovany}</td>
                    <td>{m.mentor}</td>
                    <td className="num editable">
                      <CellEdit
                        value={(m.pct * 100).toFixed(2)}
                        step="0.1"
                        onCommit={(v) => zmenMentoring(m, { pct: cislo(v) / 100 })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={m.od || ''}
                        onChange={(e) => zmenMentoring(m, { od: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={m.do || ''}
                        title="Prázdné = mentoring běží, dokud ho někdo neukončí"
                        onChange={(e) => zmenMentoring(m, { do: e.target.value || null })}
                      />
                    </td>
                    <td>
                      {m.stav === 'running' && <span className="badge-soft b-mentor"><i className="bi bi-mortarboard"></i>běží</span>}
                      {m.stav === 'planned' && <span className="badge-soft b-neutral"><i className="bi bi-hourglass"></i>začne později</span>}
                      {m.stav === 'ended' && <span className="badge-soft b-neutral"><i className="bi bi-check2"></i>skončil</span>}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        title="Zrušit mentoring"
                        onClick={() => handleSmazMentoring(m)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
                {!mentoring.length && (
                  <tr><td colSpan={7} className="text-center small-muted py-3">Zatím nikdo nikoho nementoruje.</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex align-items-end gap-2 mt-2 flex-wrap">
            <div>
              <label className="form-label mb-1 small-muted">Mentorovaný</label>
              <Form.Select
                size="sm"
                style={{ minWidth: '200px' }}
                value={novyMentoring.mentorovanyId}
                onChange={(e) => setNovyMentoring(p => ({ ...p, mentorovanyId: e.target.value }))}
              >
                <option value="">Vyber…</option>
                {obchodnici.filter(o => o.aktivni).map(o => (
                  <option key={o.id} value={o.id}>{o.jmeno}</option>
                ))}
              </Form.Select>
            </div>
            <div>
              <label className="form-label mb-1 small-muted">Mentor</label>
              <Form.Select
                size="sm"
                style={{ minWidth: '200px' }}
                value={novyMentoring.mentorId}
                onChange={(e) => setNovyMentoring(p => ({ ...p, mentorId: e.target.value }))}
              >
                <option value="">Vyber…</option>
                {obchodnici
                  .filter(o => o.aktivni && String(o.id) !== String(novyMentoring.mentorovanyId))
                  .map(o => <option key={o.id} value={o.id}>{o.jmeno}</option>)}
              </Form.Select>
            </div>
            <div>
              <label className="form-label mb-1 small-muted">Sazba %</label>
              <Form.Control
                type="number"
                step="0.1"
                size="sm"
                style={{ width: '90px' }}
                value={novyMentoring.pct}
                onChange={(e) => setNovyMentoring(p => ({ ...p, pct: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label mb-1 small-muted">Od</label>
              <Form.Control
                type="date"
                size="sm"
                value={novyMentoring.od}
                onChange={(e) => setNovyMentoring(p => ({ ...p, od: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label mb-1 small-muted">Do (nepovinné)</label>
              <Form.Control
                type="date"
                size="sm"
                value={novyMentoring.do}
                onChange={(e) => setNovyMentoring(p => ({ ...p, do: e.target.value }))}
              />
            </div>
            <Button variant="outline-primary" size="sm" onClick={handlePridejMentoring}>
              <i className="bi bi-plus-lg me-1"></i>Přidat
            </Button>
          </div>

          <div className="chart-caption">
            Mentor bere procento z úhrad mentorovaného po dobu, co ho zaučuje — <strong>bez krácení
            za opožděnou platbu</strong> a jen ze skutečných faktur, ne z ručních položek. Rozhoduje
            datum úhrady faktury, takže mentoring může začít i skončit uprostřed měsíce. Prázdné „do"
            znamená, že běží dál. Dva mentory naráz mít jeden člověk nemůže; když je mentor zároveň
            jeho manažer, dostane superprovizi i mentoring.
          </div>
        </div>
      </div>
    </Karta>
  );

  /* ------------------------------------------------ 3) plány titulů ----- */
  /* ------------------------------------------ plány jednotlivých vydání - */

  const nactiVydani = useCallback(async (titulId, posun, vse) => {
    setNacitamVydani(true);
    try {
      const okno = vse ? {} : oknoVydani(posun);
      setVydani(await nactiVydaniTitulu(titulId, okno));
    } catch (e) {
      toast(e.message || 'Vydání se nepodařilo načíst.', 'danger');
      setVydani(null);
    } finally {
      setNacitamVydani(false);
    }
  }, []);

  /* Pozor: `t.id` je kód titulu (`T-KLIC`), číselné id drží `t.dbId` — API
     i oprava plánu jedou na číselné. */
  const prepniTitul = (titulId) => {
    if (rozbalenyTitul === titulId) {
      setRozbalenyTitul(null);
      setVydani(null);
      return;
    }
    setRozbalenyTitul(titulId);
    setVydani(null);
    setOknoPosun(0);
    setVsechnaVydani(false);
    nactiVydani(titulId, 0, false);
  };

  const posunOkno = (posun, vse = false) => {
    setOknoPosun(posun);
    setVsechnaVydani(vse);
    nactiVydani(rozbalenyTitul, posun, vse);
  };

  /** Hranice vydání: ruční oprava, jinak zděděný plán titulu. */
  const hranice = (v, titul, klic) => {
    const prepsano = v.prepis?.[klic];
    return prepsano != null ? prepsano : (titul.plan?.[klic] ?? 0);
  };

  /** Zápis jedné hranice. Prázdná hodnota se vrací na plán titulu. */
  const upravVydani = async (v, klic, hodnota) => {
    const nova = String(hodnota).trim() === '' ? null : cislo(hodnota);
    const puvodni = v.prepis?.[klic] ?? null;
    if (nova === puvodni) return;

    // Překreslí se hned, server potvrdí vzápětí — stejně jako u plánů titulu.
    setVydani(prev => ({
      ...prev,
      vydani: prev.vydani.map(x => (x.klic === v.klic
        ? { ...x, prepis: { ...(x.prepis || {}), [klic]: nova } }
        : x)),
    }));

    try {
      await ulozPlanVydani(v.klic, { [klic]: nova });
      toast('Plán vydání uložen.');
    } catch (e) {
      toast(e.message || 'Uložení selhalo.', 'danger');
      nactiVydani(rozbalenyTitul, oknoPosun, vsechnaVydani);
    }
  };

  const vratVydani = async (v) => {
    try {
      await zrusPlanVydani(v.klic);
      setVydani(prev => ({
        ...prev,
        vydani: prev.vydani.map(x => (x.klic === v.klic ? { ...x, prepis: null } : x)),
      }));
      toast('Vydání se vrátilo na plán titulu.');
    } catch (e) {
      toast(e.message || 'Uložení selhalo.', 'danger');
    }
  };

  /** Vnořený výpis vydání pod rozbaleným titulem. */
  const radekVydani = (t) => {
    const titul = vydani?.titul;
    const agregovany = titul?.agregovany;

    return (
      <tr className="stat-issues-row">
        <td colSpan={11} className="p-0">
          <div className="stat-issues">
            <div className="stat-issues-head">
              <strong>Plány jednotlivých vydání — {t.nazev}</strong>
              {vydani && (
                <span className="small-muted ms-2">
                  {vsechnaVydani
                    ? `všechna vydání (${vydani.vydani.length} z ${vydani.celkem})`
                    : `${naCesky(oknoVydani(oknoPosun).od)} – ${naCesky(oknoVydani(oknoPosun).do)}`}
                </span>
              )}
              <div className="ms-auto d-flex gap-1">
                {/* Šipky posouvají dvouleté okno po rocích; při výpisu všech
                    vydání nemají co posouvat. */}
                <Button size="sm" variant="outline-secondary" title="O rok zpět"
                  disabled={vsechnaVydani}
                  onClick={() => posunOkno(oknoPosun - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </Button>
                <Button size="sm" variant="outline-secondary" title="O rok vpřed"
                  disabled={vsechnaVydani}
                  onClick={() => posunOkno(oknoPosun + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </Button>
                <Button size="sm" variant={vsechnaVydani ? 'secondary' : 'outline-secondary'}
                  onClick={() => posunOkno(0, !vsechnaVydani)}>
                  {vsechnaVydani ? 'Zpět na dva roky' : 'Všechna vydání'}
                </Button>
              </div>
            </div>

            {agregovany && (
              <Alert variant="info" className="mb-2 py-2 small">
                <i className="bi bi-info-circle me-1"></i>
                {titul.mesicniAgregace
                  ? 'Titul se počítá měsíčně — pásmo vychází z objemu všech vydání v měsíci, ne z jednoho vydání.'
                  : 'Plán titulu se skládá z plánů mutací, ne z jednotlivých vydání.'}
                {' '}Oprava jednotlivého vydání by se proto v sestavě neprojevila, výpis je jen ke čtení.
              </Alert>
            )}

            {nacitamVydani && <div className="py-3 text-center"><Spinner size="sm" animation="border" /></div>}

            {!nacitamVydani && vydani && vydani.vydani.length === 0 && (
              <div className="py-3 small-muted">V tomhle období nemá titul žádné vydání.</div>
            )}

            {!nacitamVydani && vydani && vydani.vydani.length > 0 && (
              <Table hover size="sm" className="align-middle stat-table mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: '220px' }}>Vydání</th>
                    <th>Datum vydání</th>
                    <th className="num">Objem NET NET</th>
                    <th className="num">11 %</th>
                    <th className="num">14 %</th>
                    <th className="num">17 %</th>
                    <th className="num">20 %</th>
                    <th style={{ minWidth: '150px' }}>Plán</th>
                  </tr>
                </thead>
                <tbody>
                  {vydani.vydani.map(v => {
                    const prepsano = !!v.prepis && ['p11', 'p14', 'p17', 'p20'].some(k => v.prepis[k] != null);
                    return (
                      <tr key={v.klic} className={prepsano ? 'radek-prepsany' : ''}>
                        <td>
                          <div>{v.nazev}</div>
                          {v.medium !== t.nazev && <span className="small-muted">{v.medium}</span>}
                        </td>
                        <td className="mono">{naCesky(v.datum)}</td>
                        <td className="num small-muted">{fmtKc(v.objem)}</td>
                        {['p11', 'p14', 'p17', 'p20'].map(k => (
                          <td key={k}
                            className={`num ${agregovany || !vydani.muzeUpravovat ? '' : 'editable'} ${v.prepis?.[k] != null ? 'hodnota-prepsana' : ''}`}
                            title={v.prepis?.[k] != null
                              ? `Ručně opravený plán · plán titulu je ${fmtKc(titul.plan?.[k] || 0)}`
                              : 'Zděděno z plánu titulu'}>
                            {agregovany || !vydani.muzeUpravovat
                              ? fmtKc(hranice(v, titul, k))
                              : (
                                <CellEdit
                                  value={hranice(v, titul, k)}
                                  step="1000"
                                  onCommit={(hodnota) => upravVydani(v, k, hodnota)}
                                />
                              )}
                          </td>
                        ))}
                        <td>
                          {prepsano ? (
                            <>
                              <span className="badge-soft b-warn">
                                <i className="bi bi-pencil-fill"></i>ručně
                              </span>
                              {vydani.muzeUpravovat && !agregovany && (
                                <Button size="sm" variant="link" className="p-0 ms-2 align-baseline"
                                  onClick={() => vratVydani(v)}>
                                  vrátit na plán titulu
                                </Button>
                              )}
                            </>
                          ) : <span className="small-muted">podle titulu</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const zalozkaPlany = () => (
    <Karta nadpis="Plány titulů (hranice objemu pro pásma provize)">
      <div className="table-responsive stat-table-wrap">
        <Table hover className="align-middle stat-table">
          <thead className="table-light">
            <tr>
              <th style={{ width: '38px' }}></th>
              <th className="col-sticky" style={{ minWidth: '220px' }}>Titul</th>
              <th>Kraj</th>
              <th>Periodicita</th>
              <th className="num">Stran</th>
              <th className="num">11 %</th>
              <th className="num">14 %</th>
              <th className="num">17 %</th>
              <th className="num">20 %</th>
              <th>Významné médium</th>
              <th style={{ minWidth: '300px' }}>Úpravy plánů / poznámka</th>
            </tr>
          </thead>
          <tbody>
            {naseTituly.map(t => (
              <React.Fragment key={t.id}>
              <tr>
                {/* Rozbalení vypíše jednotlivá vydání titulu i s jejich plány. */}
                <td>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 text-decoration-none"
                    title={rozbalenyTitul === t.dbId ? 'Sbalit vydání' : 'Zobrazit jednotlivá vydání'}
                    onClick={() => prepniTitul(t.dbId)}
                  >
                    <i className={`bi ${rozbalenyTitul === t.dbId ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                  </Button>
                </td>
                <td className="col-sticky">
                  <div className="fw-semibold">{t.nazev}</div>
                  <span className="small-muted" style={{ fontSize: '.72rem' }}>{t.lokalita}</span>
                  {t.planNeurcen && (
                    <span className="badge-soft b-crit ms-1">
                      <i className="bi bi-exclamation-triangle-fill"></i>nenastaveno
                    </span>
                  )}
                  {t.bezBonusu && (
                    <span className="badge-soft b-warn ms-1"><i className="bi bi-slash-circle"></i>bez bonusu</span>
                  )}
                  {t.mesicniAgregace && (
                    <span className="badge-soft b-neutral ms-1">
                      <i className="bi bi-calendar-week"></i>měsíční agregace
                    </span>
                  )}
                </td>
                <td className="small-muted">{t.kraj}</td>
                <td className="small-muted">{t.periodicita}</td>
                <td className="num editable">
                  <CellEdit value={t.stran || ''} onCommit={(v) => upravTitul(t.id, { stran: cislo(v) || null })} />
                </td>
                {['p11', 'p14', 'p17', 'p20'].map(k => (
                  <td key={k} className="num editable">
                    <CellEdit value={t.plany[k] || 0} step="1000" onCommit={(v) => upravPlan(t.id, k, v)} />
                  </td>
                ))}
                <td>
                  {t.vyznamne
                    ? <span className="badge-soft b-nase">ano · na {t.vyznamneNa} %</span>
                    : <span className="small-muted">ne</span>}
                </td>
                <td className="small-muted" style={{ fontSize: '.78rem' }}>{t.poznamka || '—'}</td>
              </tr>
              {rozbalenyTitul === t.dbId && radekVydani(t)}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="sec-title mt-4">Plány mutací Novin kraje a Kraje Vysočina</div>
      <div className="row g-3">
        <div className="col-12 col-lg-5">
          <Table hover className="align-middle stat-table">
            <thead className="table-light">
              <tr>
                <th>Velikost mutace</th>
                <th>Náklad</th>
                <th className="num">14 %</th>
                <th className="num">17 %</th>
                <th className="num">20 %</th>
              </tr>
            </thead>
            <tbody>
              {velikostiMutaci.map(v => (
                <tr key={v.id}>
                  <td className="fw-semibold">{v.label}</td>
                  <td className="small-muted">{mutacePlany[v.id].naklad}</td>
                  {['p14', 'p17', 'p20'].map(f => (
                    <td key={f} className="num editable">
                      <CellEdit
                        value={mutacePlany[v.id][f]}
                        step="5000"
                        onCommit={(raw) => {
                          setMutacePlany(prev => ({ ...prev, [v.id]: { ...prev[v.id], [f]: cislo(raw) } }));
                          toast('Plán mutace upraven.');
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="chart-caption">
            {MUTACE_POZNAMKY.map(p => <div key={p}>{p}</div>)}
          </div>
        </div>

        <div className="col-12 col-lg-7">
          <Table hover className="align-middle stat-table">
            <thead className="table-light">
              <tr>
                <th>Mutace</th>
                <th>Titul</th>
                <th>Velikost</th>
                <th className="num">Stran</th>
                <th className="num">Plán 14 %</th>
              </tr>
            </thead>
            <tbody>
              {mutace.map(m => (
                <tr key={m.id}>
                  <td className="fw-semibold">{m.nazev}</td>
                  <td className="small-muted">
                    {m.titulId === 'T-NK' ? 'Noviny kraje' : 'Kraj Vysočina'}
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      style={{ width: 'auto', display: 'inline-block' }}
                      value={m.velikost}
                      onChange={(e) => {
                        const velikost = e.target.value;
                        setMutace(prev => prev.map(x => (x.id === m.id ? { ...x, velikost } : x)));
                        ulozMedium(m.id, { titulId: titulDbId(m.titulId), velikost, stran: m.stran })
                          .then(() => toast('Velikost mutace změněna.'))
                          .catch(e2 => toast(e2.message || 'Uložení selhalo.', 'danger'));
                      }}
                    >
                      <option value="">—</option>
                      {velikostiMutaci.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                    </Form.Select>
                  </td>
                  <td className="num editable">
                    <CellEdit
                      value={m.stran ?? ''}
                      onCommit={(v) => {
                        const stran = cislo(v) || null;
                        setMutace(prev => prev.map(x => (x.id === m.id ? { ...x, stran } : x)));
                        ulozMedium(m.id, { titulId: titulDbId(m.titulId), velikost: m.velikost, stran })
                          .catch(e2 => toast(e2.message || 'Uložení selhalo.', 'danger'));
                      }}
                    />
                  </td>
                  <td className="num">{m.velikost && mutacePlany[m.velikost] ? fmtKc(mutacePlany[m.velikost].p14) : '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Součet plánů mutací Novin kraje (14 %)</td>
                <td className="num">{fmtKc(soucetPlanuNK)}</td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </div>
    </Karta>
  );

  /* --------------------------------------------- 4) maximální slevy ----- */
  const zalozkaSlevy = () => (
    <Karta nadpis="Kompetence při poskytování slev">
      <div className="row g-3 align-items-end mb-3">
        <div className="col-12 col-md-3">
          <label className="form-label">Hledat</label>
          <Form.Control
            placeholder="Titul, kraj, lokalita…"
            value={hledatSlevy}
            onChange={(e) => setHledatSlevy(e.target.value)}
          />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label">Typ média</label>
          <Form.Select value={typSlevy} onChange={(e) => setTypSlevy(e.target.value)}>
            <option value="">Všechny typy</option>
            <option value="nase">Naše média</option>
            <option value="partner">Partnerská média</option>
          </Form.Select>
        </div>
      </div>

      <div className="table-responsive stat-table-wrap">
        <Table hover className="align-middle stat-table">
          <thead className="table-light">
            <tr>
              <th>Kraj</th>
              <th>Lokalita</th>
              <th style={{ minWidth: '200px' }}>Titul</th>
              <th>Periodicita</th>
              <th className="num">Sleva obchodník</th>
              <th className="num">Sleva manažer titulu</th>
              <th className="num">Sleva manažer regionu</th>
              <th className="num">Maximum bez souhlasu OŘ</th>
              <th>Provize</th>
            </tr>
          </thead>
          <tbody>
            {titulySeSlevami.map(t => (
              <tr key={t.id}>
                <td className="small-muted">{t.kraj}</td>
                <td className="small-muted">{t.lokalita}</td>
                <td className="fw-semibold">{t.nazev}</td>
                <td className="small-muted">{t.periodicita}</td>
                {['oz', 'manazerTitulu', 'manazerRegionu', 'maxBezSouhlasu'].map(k => (
                  <td key={k} className="num editable">
                    {t.slevy[k] == null
                      ? <span className="small-muted">{t.slevaPozn || '—'}</span>
                      : <CellEdit value={t.slevy[k]} step="5" onCommit={(v) => upravSlevu(t.id, k, v)} />}
                  </td>
                ))}
                <td>
                  {t.typ === 'partner'
                    ? <span className="badge-soft b-partner">{fmtPct(t.zakladProvize, 0)}</span>
                    : <span className="badge-soft b-nase">{t.provizePozn || 'běžná'}</span>}
                </td>
              </tr>
            ))}
            {titulySeSlevami.length === 0 && (
              <tr><td colSpan={9} className="empty-state">Žádný titul neodpovídá filtru.</td></tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className="alert alert-light border mt-3 py-2 small">
        <i className="bi bi-info-circle me-1"></i>
        Poskytne-li obchodník vyšší slevu bez souhlasu příslušného manažera, vystavuje se riziku vyřazení
        inzerátu z konkrétního vydání. U partnerských titulů následuje <strong>krácení provize</strong>:
        za každý 1 p.b. slevy nad maximum je provize nižší o{' '}
        {(zaklady.penalizace * 100).toLocaleString('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} p.b.
      </div>
    </Karta>
  );

  /* ------------------------------------------ 5) korekce splatnosti ----- */
  const zalozkaSplatnost = () => (
    <Karta
      nadpis="Korekce za pozdní úhradu"
      popis="Závěrečná korekce se aplikuje až na součet provize z vydání a bonusové provize"
    >
      <div className="row g-4">
        <div className="col-12 col-lg-8">
          <Table hover className="align-middle stat-table">
            <thead className="table-light">
              <tr>
                <th>Pásmo</th>
                <th className="num" style={{ minWidth: '130px' }}>Do dnů po splatnosti</th>
                <th className="num" style={{ minWidth: '110px' }}>Koeficient</th>
                <th>Dopad</th>
              </tr>
            </thead>
            <tbody>
              {korekce.map((k, i) => (
                <tr key={k.kod}>
                  <td>
                    <span className={`dpd-chip ${k.kod}`}>
                      <i className={`bi ${k.ikona}`}></i>{k.nazev}
                    </span>
                  </td>
                  <td className="num editable">
                    {k.do == null
                      ? <span className="small-muted">a více</span>
                      : (
                        <CellEdit
                          value={k.do}
                          onCommit={(v) => {
                            setKorekce(prev => prev.map((x, j) => (j === i ? { ...x, do: cislo(v) } : x)));
                            toast('Korekce upravena.');
                          }}
                        />
                      )}
                  </td>
                  <td className="num editable">
                    <CellEdit
                      value={k.koef}
                      step="0.1"
                      onCommit={(v) => {
                        setKorekce(prev => prev.map((x, j) => (j === i ? { ...x, koef: cislo(v) } : x)));
                        toast('Korekce upravena.');
                      }}
                    />
                  </td>
                  <td className="small-muted">{k.popis}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="alert alert-light border py-2 small mt-3">
            <i className="bi bi-info-circle me-1"></i>
            I při 100% krácení se úhrada <strong>stále počítá do měsíčního obratu obchodníka</strong>{' '}
            pro určení bonusové provize.
          </div>
        </div>
      </div>
    </Karta>
  );

  /* ------------------------------------------- 6) klasifikace médií ----- */
  const zalozkaMedia = () => (
    <Karta
      nadpis="Klasifikace médií"
      popis="Zařazení titulu určuje, jak se počítá provize a zda se započítává do obratu pro bonus"
    >
      <div className="row g-3 mb-3">
        {KLASIFIKACE_MEDII.map(k => (
          <div className="col-12 col-lg-4" key={k.id}>
            <div className="title-card">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className={`badge-soft ${k.cls}`}><i className={`bi ${k.ikona}`}></i>{k.nazev}</span>
                <span className="ms-auto fs-5 fw-bold">{pocetPodleTypu(k.id)}</span>
              </div>
              <div className="small-muted">{k.popis}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="table-responsive stat-table-wrap">
        <Table hover className="align-middle stat-table">
          <thead className="table-light">
            <tr>
              <th className="col-sticky" style={{ minWidth: '230px' }}>Titul</th>
              <th>Kraj</th>
              <th>Periodicita</th>
              <th>Zařazení</th>
              <th className="num" style={{ minWidth: '130px' }}>Základ provize %</th>
              <th>Bonusová provize</th>
            </tr>
          </thead>
          <tbody>
            {tituly.map(t => (
              <tr key={t.id}>
                <td className="col-sticky fw-semibold">{t.nazev}</td>
                <td className="small-muted">{t.kraj}</td>
                <td className="small-muted">{t.periodicita}</td>
                <td>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={t.typ}
                    onChange={(e) => {
                      const typ = e.target.value;
                      upravTitul(t.id, {
                        typ,
                        zakladProvize: typ === 'nase'
                          ? t.zakladProvize
                          : (t.zakladProvize || (typ === 'partner' ? zaklady.partner : zaklady.special)),
                      });
                      toast('Zařazení titulu změněno.');
                    }}
                  >
                    <option value="nase">Naše médium</option>
                    <option value="partner">Partnerské médium</option>
                    <option value="special">Speciální operace</option>
                  </Form.Select>
                </td>
                <td className="num editable">
                  {t.typ === 'nase'
                    ? <span className="small-muted">dle plnění plánu</span>
                    : (
                      <CellEdit
                        value={((t.zakladProvize || 0) * 100).toFixed(1)}
                        step="0.5"
                        onCommit={(v) => upravTitul(t.id, { zakladProvize: cislo(v) / 100 })}
                      />
                    )}
                </td>
                <td>
                  {t.typ === 'nase'
                    ? (t.bezBonusu
                      ? <span className="badge-soft b-warn"><i className="bi bi-slash-circle"></i>bez bonusu</span>
                      : <span className="badge-soft b-good"><i className="bi bi-check-circle-fill"></i>započítává se</span>)
                    : <span className="badge-soft b-neutral">nezapočítává se</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Karta>
  );

  const ZALOZKY = {
    procenta: zalozkaProcenta,
    bonus: zalozkaBonus,
    plany: zalozkaPlany,
    slevy: zalozkaSlevy,
    splatnost: zalozkaSplatnost,
    media: zalozkaMedia,
  };

  /* -------------------------------------------------------------- render - */
  if (nacitam) {
    return (
      <Container fluid className="px-4 py-5 text-center statistics-module">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 statistics-module stat-settings">
      {/* Hlavička */}
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center g-2">
            <div className="col-12">
              <h4 className="mb-0"><i className="bi bi-sliders me-2"></i>Nastavení sazeb a číselníků</h4>
            </div>
          </div>
        </Container>
      </div>

      {chyba && <Alert variant="danger" dismissible onClose={() => setChyba('')}>{chyba}</Alert>}

      {nezarazenaMedia > 0 && (
        <Alert variant="warning" className="py-2">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          Nezařazených médií: <strong>{nezarazenaMedia}</strong> — do provizí nevstupují.
          Zařadit je můžete v záložce <button type="button" className="btn btn-link p-0 align-baseline"
            onClick={() => setAktivni('media')}>Klasifikace médií</button>.
        </Alert>
      )}

      {/* Platnost verze sazeb */}
      <div className="lock-strip">
        <i className="bi bi-clock-history ls-icon"></i>
        <strong>Verze sazeb platná od {naCesky(platnostOd)}.</strong>
        <span className="ms-2 small-muted">
          Úprava sazeb se ukládá jako nová verze — stará zůstane, aby šlo uzavřené
          období přepočítat sazbami, které pro ně platily.
        </span>
        <div className="ms-auto d-flex gap-2 no-print">
          <Form.Control
            type="date"
            size="sm"
            style={{ width: 'auto' }}
            value={novaPlatnost}
            onChange={(e) => setNovaPlatnost(e.target.value)}
            title="Datum, od kterého má nová verze sazeb platit"
          />
          <Button
            variant="primary" size="sm" className="text-nowrap"
            onClick={() => ulozCast(
              { pasma, zaklady, bonusPasma, korekce, mutacePlany },
              `Sazby uloženy do verze platné od ${naCesky(platnostOd)}.`,
            )}
            title="Přepíše hodnoty ve stávající verzi sazeb"
          >
            <i className="bi bi-check-lg me-1"></i>Uložit sazby
          </Button>
          <Button variant="outline-primary" size="sm" className="text-nowrap" onClick={handleNovaVerze}>
            <i className="bi bi-plus-lg me-1"></i>Nová verze sazeb
          </Button>
        </div>
      </div>

      {/* Záložky */}
      <div className="stat-settings-tabs-wrapper no-print">
        <Nav variant="tabs" className="stat-settings-tabs" activeKey={aktivni} onSelect={setAktivni}>
          {TABY.map(t => (
            <Nav.Item key={t.id}>
              <Nav.Link eventKey={t.id}>
                <i className={`bi ${t.ikona} me-1`}></i>{t.nazev}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>

      <div className="stat-settings-body">
        {ZALOZKY[aktivni]()}
      </div>
    </Container>
  );
}

export default StatisticsSettings;
